/** @jsx h */
import { h, JSX } from 'preact';
import { sendAnalyticsTrack } from '../../../../../common/js/TrackAnalyticsHelper';
import VastIcon from '../../vast-icon';
import { RootStore } from '../../../../../common/js/stores/root.store';
import { MpuControlsStrategy } from '../../../../../common/js/stores/controls/mpu-controls-strategy';
import { VastIconType } from '../../../lib/vast/vast-icon';
import OptOutSmallIcon from '../../../../../common/components/opt-out/small-icon';
import PoweredByOgury from '../../../../../common/components/powered-by-ogury';
import CtaControl from '../../../../../common/components/cta-control';
import CountdownTimer from '../../../../../common/components/countdown-timer';
import ProgressBar from '../../../../../common/components/progress-bar';
import BrandBar from '../../../../../common/components/brand-bar';
import SoundControl from '../../../../../common/components/sound-control';
import CustomizedControl from '../../../../../common/components/customized-control';

interface ColorsConfig {
  primaryColor: string;
  secondaryColor: string;
}

interface CtaConfig {
  label: string;
  animationDelay: number;
}

interface MpuProps {
  colors: ColorsConfig;
  cta: CtaConfig;
  elapsedTime: number;
  onOptout: () => void;
  videoDuration: number;
  onToggleMute: (e: Event) => void;
  isVideoMuted: boolean;
  remainingTime: number;
  vastIcon?: VastIconType;
  onClickVastIcon?: (url: string) => void;
}

export default function Mpu({
  colors,
  cta,
  elapsedTime,
  onOptout,
  videoDuration,
  onToggleMute,
  isVideoMuted,
  remainingTime,
  vastIcon,
  onClickVastIcon
}: MpuProps): JSX.Element {
  const controlsStore: MpuControlsStrategy = RootStore.getInstance()
    .controlsStore as MpuControlsStrategy;
  const configStore = RootStore.getInstance().configStore;
  const shouldDisplayVastIcon = controlsStore.shouldDisplayVastIcon();
  const isVideoCompleted = RootStore.getInstance().videoStore.isVideoCompleted
    .value;

  const { controls } = configStore.config;

  const styles = {
    progressBar: {
      position: 'absolute' as const,
      width: '100%',
      bottom: '41px',
      left: '0px ',
      zIndex: 3
    },
    optout: {
      position: 'absolute' as const,
      top: '7px',
      right: '8px',
      zIndex: 3,
      padding: '6px',
      boxSizing: 'initial' as const
    },
    sound: {
      position: 'absolute' as const,
      bottom: '47px',
      right: '8px',
      width: '28px',
      height: '28px',
      minHeight: 0,
      padding: '7px',
      zIndex: 3
    }
  };

  const controlStyle = {
    backgroundColor: controls?.backgroundColor || '',
    backgroundOpacity:
      (controls?.backgroundOpacity && controls.backgroundOpacity / 100) || 0,
    labelColor: controls?.labelColor || '',
    labelOpacity: (controls?.labelOpacity && controls.labelOpacity / 100) || 0
  };

  let vastIconPositionStyle: Record<string, string> = {};
  if (shouldDisplayVastIcon) {
    vastIconPositionStyle = {
      top: '41px'
    };
  }

  return (
    <div>
      <BrandBar colors={colors} />

      {shouldDisplayVastIcon && vastIcon ? (
        <VastIcon
          style={vastIconPositionStyle}
          icon={vastIcon}
          onClick={onClickVastIcon}
        />
      ) : (
        ''
      )}

      {controlsStore.shouldDisplayOptOut() &&
      !controlsStore.shouldHideOptout() ? (
        <OptOutSmallIcon
          iconColor="#919499"
          iconSize="15"
          iconStyle={{
            ...styles.optout
          }}
          onClick={onOptout}
        />
      ) : (
        ''
      )}

      <CountdownTimer
        remainingTime={remainingTime}
        backgroundColor={controlStyle.backgroundColor}
        backgroundOpacity={controlStyle.backgroundOpacity}
        labelColor={controlStyle.labelColor}
        labelOpacity={controlStyle.labelOpacity}
        // countdown is at right by default - if vast icon is activated, it will be display at left
        xPosition={shouldDisplayVastIcon ? 'left' : 'right'}
      />

      {!isVideoCompleted && controlsStore.shouldDisplayMuteButton() ? (
        <CustomizedControl
          id="sound"
          onTouchStart={(e: TouchEvent) => e.stopPropagation()}
          onClick={(e: MouseEvent) => {
            e.stopPropagation();
            onToggleMute(e);
            sendAnalyticsTrack(e);
          }}
          controlStyle={controlStyle}
          fontSize={14}
          style={styles.sound}
        >
          <SoundControl iconSize={14} isMuted={isVideoMuted} />
        </CustomizedControl>
      ) : (
        ''
      )}

      {controlsStore.shouldDisplayProgressBar() && (
        <ProgressBar
          id="progress"
          adjustedFormulae={true}
          elapsedTime={elapsedTime}
          duration={videoDuration}
          style={styles.progressBar}
          onClick={(e: MouseEvent) => {
            sendAnalyticsTrack(e);
          }}
        />
      )}

      <CtaControl
        cta={cta}
        colors={colors}
        canUpdateStyle={elapsedTime >= cta.animationDelay}
      />

      <PoweredByOgury />
    </div>
  );
}
